import {
	Checkbox,
	CommandBar,
	DefaultButton,
	DetailsListLayoutMode,
	Dialog,
	DialogFooter,
	Dropdown,
	HoverCard,
	IColumn,
	ICommandBarItemProps,
	IconButton,
	IDetailsHeaderProps,
	IExpandingCardProps,
	Link,
	mergeStyleSets,
	MessageBar,
	MessageBarType,
	PrimaryButton,
	SearchBox,
	ShimmeredDetailsList,
	Stack,
	StackItem
} from "@fluentui/react"
import { SelectionMode } from "@fluentui/react/lib/Utilities"
import { debounce } from "lodash-es"
import { ChangeEvent, useEffect, useState } from "react"
import { commandBarItemStyles, getClassNames, IS_ADMIN, safeFormatDateValue, sortDetailsList } from "../lib/helper"
import { makeApiCall, makeCRMApiCall } from "../services/apiService"
import { IAccount } from "../types/IAccount"
import { CensusCount } from "./censusCount"
import { EditGroupDialog } from "./editGroupDialog"
import { GroupDocumentsDialog } from "./group-documents"
import { RequoteDialog } from "./requoteDialog"
import { FieldType, filterDataByColumnFilters, IColumnSortKeyAndDirection, IFieldFilters, IFilterTag, renderCustomListHeader } from "./Shared/DetailsListFilterCallout"
import { StickeyHeaderDetailsList } from "./Shared/StickeyHeaderDetailsList"
import { UploadCensusDialog } from "./uploadCensusDialog"

interface IAccountColumn extends IColumn {
	fieldName?: keyof IAccount | "status"
}

const classNames = mergeStyleSets({
	compactCard: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		height: "50px"
	},
	expandedCard: {
		padding: "16px 24px"
	},
	item: {
		selectors: {
			"&:hover": {
				textDecoration: "underline",
				cursor: "pointer"
			}
		}
	}
})

export const getBrokerageFriendlyName = (group: IAccount): string => {
	let retVal = "N/A"
	if (group.cw_PreferredBroker) {
		if (group.cw_Brokerage?.cw_name && group.cw_PreferredBroker.fullname) {
			retVal = `${group.cw_Brokerage["cw_name"]} - ${group.cw_PreferredBroker.fullname}`
		}
	} else if (group.cw_Brokerage?.cw_name) {
		retVal = group.cw_Brokerage["cw_name"]
	}

	return retVal
}

const milestoneFieldsOrdered = {
	cw_completedcompanyinfo: "Completed Company Info",
	cw_lockedcensus: "Locked Census",
	cw_lockunderwriting: "Lock Underwriting",
	cw_quoted: "Quoted",
	cw_markassold: "Mark as Sold",
	cw_salelost: "Sale Lost",
	cw_markedasterminated: "Marked as Terminated",
	cw_groupdeniedunderwriting: "Group Denied - Underwriting",
	cw_startenrollmentforqualifyingevents: "Start Enrollment for Qualifying Events",
	cw_renewal: "Renewal",
	cw_renewaliscomplete: "Renewal is Complete"
}

export const getMilestoneFiendlyName = (group: IAccount) => {
	let value: string = undefined
	for (let i = Object.keys(milestoneFieldsOrdered).length - 1; i >= 0; i--) {
		const key = Object.keys(milestoneFieldsOrdered)[i]
		const displayName = milestoneFieldsOrdered[key]
		const val = group[key]
		if (val) {
			value = displayName
			break
		}
	}
	return value
}

type AccountTypeQuery = "Customer" | "Prospect" | "Both"
type CompanyScopeQuery = "all" | "mine"

export function ManagementDashboard() {
	const [loading, setLoading] = useState<boolean>(true)
	const [companies, setCompanies] = useState<IAccount[]>([])
	const [filteredCompanies, setFilteredCompanies] = useState<IAccount[]>()
	const [refreshFlag, setRefreshFlag] = useState<boolean>(false)
	const [searchText, setSearchText] = useState<string>("")
	const [showNewGroupDialog, setShowNewGroupDialog] = useState<boolean>(false)
	const [showEditGroupDialog, setShowEditGroupDialog] = useState<IAccount>(undefined)
	const [showUploadCensusDialog, setShowUploadCensusDialog] = useState<IAccount>(undefined)
	const [viewDocumentsDialog, setViewDocumentsDialog] = useState<IAccount>(undefined)
	const [showEnrollmentManagerDialog, setShowEnrollmentManagerDialog] = useState<IAccount>(undefined)
	const [showRaterDialog, setShowRaterDialog] = useState<IAccount>(undefined)
	const [sortColumn, setSortColumn] = useState<IColumnSortKeyAndDirection>({ key: "name", isDescending: false })
	const [columnFilterEnabled, setColumnFilterEnabled] = useState<IColumn>()
	const [currentFilters, setCurrentFilters] = useState<IFieldFilters>({})
	const [filterTags, setFilterTags] = useState<IFilterTag[]>([])
	const username = (window.top as any).parent.Microsoft.Dynamic365.Portal.User.userName
	const accountTypeStorageKey = `${window.location.host.substring(0, window.location.host.indexOf("."))}-ACMG-AccountType-${username}`
	const brokerId = (window.top as any).parent.Microsoft.Dynamic365.Portal.User.contactId
	const localStorageAccountType = localStorage.getItem(accountTypeStorageKey) || "Prospect"
	const [accountType, setAccountType] = useState<AccountTypeQuery>((localStorageAccountType as AccountTypeQuery) || "Prospect")
	const [error, setError] = useState<string>()
	const [quoteWorkflowStep, setQuoteWorkflowStep] = useState<"ask-previous" | "search-group">(undefined)
	const [requoteGroup, setRequoteGroup] = useState<IAccount>(undefined)
	const [viewFilter, setViewFilter] = useState<CompanyScopeQuery>("all")

	const _processCompanyQuery = async (url: string, currResults: any[]) => {
		const accounts = await makeCRMApiCall(url)
		currResults = currResults.concat(accounts.value)
		if (accounts["@odata.nextLink"]) {
			return _processCompanyQuery(accounts["@odata.nextLink"].substring(accounts["@odata.nextLink"].indexOf("/accounts")), currResults)
		} else {
			return currResults
		}
	}

	const getGroupsForDashboard = async () => {
		setLoading(true)

		const storageKey = `${window.location.host.substring(0, window.location.host.indexOf("."))}-ACMG-accounts-${accountType}`
		const cachedAccounts = localStorage.getItem(storageKey)
		if (cachedAccounts) {
			setCompanies(JSON.parse(cachedAccounts))
			setFilteredCompanies(JSON.parse(cachedAccounts))
			setLoading(false)
		}
		try {
			let accounts = await makeApiCall(`/getGroupsForBroker?accountType=${accountType}`)
			accounts = accounts.filter((value, index, self) => index === self.findIndex(t => t.accountid === value.accountid))
			localStorage.setItem(storageKey, JSON.stringify(accounts))

			const activeIds = new Set(accounts.map(a => a.accountid))

			setCompanies(prev => {
				const validCompanies = prev.filter(p => activeIds.has(p.accountid))

				for (const account of accounts) {
					const existingAccount = validCompanies.find(p => p.accountid === account.accountid)
					if (!existingAccount) {
						validCompanies.push(account)
					} else {
						// Update existing account with new data
						Object.assign(existingAccount, account)
					}
				}
				return validCompanies
			})
			setFilteredCompanies(prev => {
				const validCompanies = prev.filter(p => activeIds.has(p.accountid))

				for (const account of accounts) {
					const existingAccount = validCompanies.find(p => p.accountid === account.accountid)
					if (!existingAccount) {
						validCompanies.push(account)
					} else {
						// Update existing account with new data
						Object.assign(existingAccount, account)
					}
				}
				return validCompanies
			})
		} catch (err) {
			console.log(err)
			setError("Error getting accounts")
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		getGroupsForDashboard()
	}, [refreshFlag, accountType])

	const _onClearFilter = (key: string) => {
		const currentFieldFilters = currentFilters
		delete currentFieldFilters[key] //currentFieldFilters![key]
		setCurrentFilters({ ...currentFieldFilters })
		setColumnFilterEnabled(undefined)
	}

	const sortCompanies = (a: IAccount, b: IAccount): number => {
		const column = getColumns().find(c => c.key === sortColumn.key)
		return sortDetailsList(a, b, sortColumn.key, column.data.type, sortColumn.isDescending ? "desc" : "asc")
	}

	useEffect(() => {
		let filteredCompanies = companies || []
		if (viewFilter === "mine") {
			filteredCompanies = filteredCompanies.filter(company => company?.cw_PreferredBroker?.contactid === brokerId) || []
		}
		const filterTags: IFilterTag[] = []
		if (Object.keys(currentFilters).length > 0) {
			filteredCompanies = filteredCompanies.filter(company => filterDataByColumnFilters(company, filterTags, currentFilters, getColumns, _onClearFilter))
		}

		setFilterTags(filterTags)

		if (companies && searchText.length > 2) {
			filteredCompanies = companies.filter(c => {
				return (
					c.accountnumber?.toString()?.includes(searchText) ||
					c.name?.toLowerCase()?.includes(searchText.toLowerCase()) ||
					c.cw_Brokerage?.cw_name?.toLowerCase()?.includes(searchText.toLowerCase()) ||
					getBrokerageFriendlyName(c)?.toLowerCase()?.includes(searchText.toLowerCase()) ||
					c.cw_accounttype?.join(", ").toLowerCase().includes(searchText.toLowerCase())
				)
			})
			setFilteredCompanies([...filteredCompanies].sort(sortCompanies))
		} else if (companies && searchText.length <= 2) {
			setFilteredCompanies([...filteredCompanies].sort(sortCompanies))
		}
	}, [companies, searchText, sortColumn, currentFilters, viewFilter])

	const getAccountType = (account: IAccount): string => {
		return account.cw_accounttype.join(", ")
	}

	const getColumns = (): IAccountColumn[] => {
		const columns: IAccountColumn[] = [
			{
				key: "PIN",
				fieldName: "cw_groupcode",
				name: "Pin #",
				minWidth: 60
			},
			{
				key: "name",
				fieldName: "name",
				name: "Group Name",
				minWidth: 150,
				headerClassName: "FilterableHeader-name",
				data: {
					filterable: true,
					sortable: true,
					type: FieldType.Text
				},
				onRender: (item: IAccount) => {
					return (
						<Link
							onClick={() => {
								setShowEditGroupDialog(item)
							}}>
							{item.name}
						</Link>
					)
				}
			},
			{
				key: "editControl",
				name: "",
				minWidth: 30,
				onRender: (item: IAccount) => {
					const allowUploadCensus = IS_ADMIN || (!item.cw_lockedcensus && !item.cw_lockunderwriting && !item.cw_salelost)
					const canViewEnrollment = IS_ADMIN || item.cw_accounttype.includes("Prospect")
					return (
						<IconButton
							id="ContextualMenuButton1"
							className={getClassNames().child}
							text=""
							width="30"
							split={false}
							iconProps={{ iconName: "MoreVertical" }}
							menuIconProps={{ iconName: "" }}
							menuProps={{
								shouldFocusOnMount: true,
								items: [
									{
										key: "editGroup",
										text: "View Group",
										iconProps: {
											iconName: "SingleColumnEdit"
										},
										onClick: () => setShowEditGroupDialog(item)
									},
									canViewEnrollment && {
										key: "manageEnrollment",
										text: "Manage Enrollment",
										iconProps: {
											iconName: "People"
										},
										onClick: () => setShowEnrollmentManagerDialog(item)
									},
									allowUploadCensus && {
										key: "censusUpload",
										text: "Upload Census",
										iconProps: {
											iconName: "PeopleAdd"
										},
										onClick: () => {
											setShowUploadCensusDialog(item)
										}
									},
									(IS_ADMIN || (!item.cw_lockunderwriting && !item.cw_salelost)) && {
										key: "raterGenerator",
										text: "Generate a Quote",
										iconProps: {
											iconName: "ExcelLogoInverse"
										},
										onClick: () => {
											setShowRaterDialog(item)
										}
									},
									(IS_ADMIN || item.cw_salelost) && {
										key: "requote",
										text: "Requote",
										iconProps: {
											iconName: "PageHeaderEdit"
										},
										onClick: () => {
											setRequoteGroup(item)
										}
									},
									{
										key: "viewDocuments",
										text: "View Documents",
										iconProps: {
											iconName: "DocumentSet"
										},
										onClick: () => {
											setViewDocumentsDialog(item)
										}
									}
								].filter(c => c)
							}}
						/>
					)
				}
			},
			{
				key: "cw_accounttype",
				fieldName: "status",
				name: "Status",
				headerClassName: "FilterableHeader-cw_accounttype",
				data: {
					filterable: true,
					sortable: true,
					type: FieldType.Custom,
					filterValue: getAccountType
				},
				isMultiline: true,
				minWidth: 120,
				onRender: (item: IAccount) => {
					return getAccountType(item)
				}
			},
			{
				key: "cw_coveragestartdate",
				headerClassName: "FilterableHeader-cw_coveragestartdate",
				fieldName: "cw_coveragestartdate",
				name: "Effective Date",
				minWidth: 90,
				data: {
					sortable: true,
					type: FieldType.Date
				},
				onRender: (item: IAccount) => {
					if (!item.cw_coveragestartdate) {
						return "N/A"
					}
					return safeFormatDateValue(item.cw_coveragestartdate, "MM/dd/yyyy")
				}
			},
			{
				key: "cw_completedcompanyinfo",
				name: "Milestone",
				headerClassName: "FilterableHeader-cw_completedcompanyinfo",
				minWidth: 120,
				data: {
					filterable: true,
					type: FieldType.Custom,
					sortable: true,
					filterValue: getMilestoneFiendlyName
				},
				onRender: (item: IAccount) => {
					const value = getMilestoneFiendlyName(item)
					const expandingCardProps: IExpandingCardProps = {
						onRenderCompactCard: (item: IAccount): JSX.Element => {
							return <div className={classNames.compactCard}>{item.name}</div>
						},
						onRenderExpandedCard: (item: IAccount): JSX.Element => {
							return (
								<div className={classNames.expandedCard}>
									{Object.keys(milestoneFieldsOrdered).map(key => {
										return <Checkbox key={key} label={milestoneFieldsOrdered[key]} checked={item[key]} />
									})}
								</div>
							)
						},
						renderData: item,
						styles: {
							compactCard: {
								height: 50
							}
						}
					}
					let el: JSX.Element = <span>N/A</span>
					if (value) {
						el = (
							<HoverCard styles={{ host: { whiteSpace: "normal" } }} cardOpenDelay={50} expandedCardOpenDelay={50} expandingCardProps={expandingCardProps} instantOpenOnClick>
								{value}
							</HoverCard>
						)
					}
					return el
				}
			},
			{
				key: "censusCount",
				fieldName: "censusCount",
				name: "Census Count",
				minWidth: 140,
				onRender: (item: IAccount) => {
					return <CensusCount account={item} setShowEnrollmentManagerDialog={setShowEnrollmentManagerDialog} setShowUploadCensusDialog={setShowUploadCensusDialog} />
				}
			},
			{
				key: "cw_PreferredBroker",
				fieldName: "cw_PreferredBroker",
				name: "Agency",
				headerClassName: "FilterableHeader-cw_PreferredBroker",
				data: {
					filterable: true,
					sortable: true,
					type: FieldType.Custom,
					filterValue: getBrokerageFriendlyName
				},
				minWidth: 150,
				isMultiline: true,
				onRender: (item: IAccount) => {
					return getBrokerageFriendlyName(item)
				}
			}
		].filter(Boolean) as IAccountColumn[]

		return columns
	}

	const getCommandBarItems = (): ICommandBarItemProps[] => {
		const items: ICommandBarItemProps[] = [
			IS_ADMIN
				? {
						key: "newGroup",
						text: "Add A New Group",
						commandBarButtonAs: () => {
							return <PrimaryButton style={{ marginRight: 10, height: 32 }} text="Add a New Group" onClick={() => setShowNewGroupDialog(true)} />
						}
					}
				: {
						key: "requestQuote",
						text: "Request Quote",
						commandBarButtonAs: () => {
							return <PrimaryButton style={{ marginRight: 10, height: 32 }} text="Request Quote" onClick={() => setQuoteWorkflowStep("ask-previous")} />
						}
					},
			{
				key: "accountTypeDropdown",
				text: "Type",
				commandBarButtonAs: () => {
					const availableOptions = ["Customer", "Prospect", "Both"]
					return (
						<Dropdown
							calloutProps={{ calloutWidth: 100 }}
							options={availableOptions.map(o => ({ key: o, text: o }))}
							selectedKey={accountType}
							onChange={(_ev, option) => {
								setAccountType(option.key.toString() as AccountTypeQuery)
								localStorage.setItem(accountTypeStorageKey, option.key.toString())
							}}
						/>
					)
				}
			}
			// {
			// 	key: "downloadToExcel",
			// 	text: "Download to Excel",
			// 	commandBarButtonAs: () => {
			// 		return <PrimaryButton style={{ marginRight: 10 }} text="Download to Excel" onClick={() => alert("COMING SOON")} />
			// 	}
			// }
		]
		return items
	}

	const getCommandBarFarItems = (): ICommandBarItemProps[] => {
		const items: ICommandBarItemProps[] = [
			/*{
				key: "filter",
				commandBarButtonAs: () => {
					return (
						<Dropdown
							style={{ marginRight: 16 }}
							options={[
								{
									key: "Active",
									text: "Active"
								},
								{
									key: "All",
									text: "All"
								}
							]}
							selectedKey={companiesFilter}
							onChange={(_ev, option) => {
								setCompaniesFilter(option.key?.toString() as CompanyFilter)
							}}
						/>
					)
				}
			},*/
			{
				key: "filter",
				commandBarButtonAs: () => {
					return (
						<Dropdown
							selectedKey={viewFilter}
							options={[
								{ key: "all", text: "All Groups" },
								{ key: "mine", text: "My Groups" }
							]}
							style={{ marginRight: 16 }}
							onChange={(_ev, option) => {
								setViewFilter(option.key?.toString() as CompanyScopeQuery)
							}}
						/>
					)
				}
			},
			{
				key: "search",
				commandBarButtonAs: () => {
					return (
						<div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
							<SearchBox
								disableAnimation
								placeholder="Search..."
								underlined
								defaultValue={searchText}
								onChange={debounce((_: ChangeEvent<HTMLInputElement> | undefined, newValue?: string | undefined) => setSearchText(newValue || ""), 350)}
								styles={{ root: { width: 300 } }}
							/>
						</div>
					)
				},
				buttonStyles: commandBarItemStyles
			}
		]
		return items
	}

	return (
		<>
			<Stack tokens={{ childrenGap: 10 }} style={{ padding: 15 }}>
				{error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
				{loading && <ShimmeredDetailsList items={[]} columns={getColumns()} enableShimmer />}
				{!loading && filteredCompanies && (
					<Stack tokens={{ childrenGap: 10 }}>
						<CommandBar items={getCommandBarItems()} farItems={getCommandBarFarItems()} />
						{Object.keys(currentFilters).length > 0 && (
							<>
								<Stack.Item>
									<Stack horizontal wrap tokens={{ childrenGap: 8 }}>
										<StackItem styles={{ root: { display: "flex", alignItems: "center" } }}>Current Filters</StackItem>
										{filterTags.map((ft, ind) => {
											const iconProps = ft.onClick && { iconName: "Cancel" }
											// const rootStyles = mergeStyles(itemStyles.root, {
											// 	padding: "0px 4px",
											// 	backgroundColor: "rgba(237, 235, 233, .5)"
											// });
											return (
												<StackItem key={ind}>
													<DefaultButton
														title={`${ft.description}${ft.onClick && " Click to remove this filter."}`}
														styles={{ /*root: rootStyles,*/ flexContainer: { flexDirection: "row-reverse" } }}
														iconProps={iconProps}
														onClick={ft.onClick}>
														{ft.text}
													</DefaultButton>
												</StackItem>
											)
										})}
									</Stack>
								</Stack.Item>
							</>
						)}
						<StickeyHeaderDetailsList
							items={filteredCompanies}
							columns={getColumns()}
							layoutMode={DetailsListLayoutMode.fixedColumns}
							selectionMode={SelectionMode.none}
							onRenderDetailsHeader={(props?: IDetailsHeaderProps, defaultRender?: (props?: IDetailsHeaderProps) => JSX.Element | null) =>
								renderCustomListHeader(columnFilterEnabled, setColumnFilterEnabled, companies, currentFilters, setCurrentFilters, sortColumn, setSortColumn, props, defaultRender)
							}
						/>
					</Stack>
				)}
			</Stack>
			{showNewGroupDialog && (
				<EditGroupDialog
					closePanel={refreshItems => {
						setShowNewGroupDialog(false)
						if (refreshItems) setRefreshFlag(!refreshFlag)
					}}
				/>
			)}
			{showEditGroupDialog && (
				<EditGroupDialog
					closePanel={refreshItems => {
						setShowEditGroupDialog(undefined)
						if (refreshItems) setRefreshFlag(!refreshFlag)
					}}
					initialGroup={showEditGroupDialog}
				/>
			)}
			{showUploadCensusDialog && (
				<UploadCensusDialog
					closePanel={(refreshItems, navigateToEnrollmentManager) => {
						if (navigateToEnrollmentManager) setShowEnrollmentManagerDialog(showUploadCensusDialog)
						setShowUploadCensusDialog(undefined)
						if (refreshItems) setRefreshFlag(!refreshFlag)
					}}
					group={showUploadCensusDialog}
				/>
			)}
			{viewDocumentsDialog && <GroupDocumentsDialog closePanel={() => setViewDocumentsDialog(undefined)} group={viewDocumentsDialog} />}
			{showEnrollmentManagerDialog && (
				<EditGroupDialog
					closePanel={(refresh: boolean) => {
						if (refresh) setRefreshFlag(!refreshFlag)
						setShowEnrollmentManagerDialog(undefined)
					}}
					initialGroup={showEnrollmentManagerDialog}
					selectedTab="enrollmentManager"
				/>
			)}
			{showRaterDialog && <EditGroupDialog initialGroup={showRaterDialog} closePanel={() => setShowRaterDialog(undefined)} selectedTab="quoteGenerator" />}
			{quoteWorkflowStep === "ask-previous" && (
				<Dialog hidden={false} onDismiss={() => setQuoteWorkflowStep(undefined)} dialogContentProps={{ title: "Request Quote" }}>
					<p>Have you previously quoted this group with ACMG?</p>
					<DialogFooter>
						<PrimaryButton text="Yes" onClick={() => setQuoteWorkflowStep("search-group")} />
						<DefaultButton
							text="No"
							onClick={() => {
								setQuoteWorkflowStep(undefined)
								setShowNewGroupDialog(true)
							}}
						/>
					</DialogFooter>
				</Dialog>
			)}
			{quoteWorkflowStep === "search-group" && (
				<Dialog hidden={false} onDismiss={() => setQuoteWorkflowStep(undefined)} dialogContentProps={{ title: "Select a Group" }}>
					<div style={{ maxHeight: 300, overflowY: "auto", marginTop: 15 }}>
						{companies.filter(company => company.cw_salelost === true).length > 0 ? (
							companies
								.filter(company => company.cw_salelost === true)
								.map(company => (
									<DefaultButton
										key={company.accountid}
										text={company.name}
										onClick={() => {
											setRequoteGroup(company)
										}}
										disabled={!company.cw_salelost}
										style={{ width: "100%", textAlign: "left", marginBottom: 4 }}></DefaultButton>
								))
						) : (
							<p style={{ textAlign: "center", padding: 20 }}>No groups found</p>
						)}
					</div>
					<DialogFooter styles={{ actionsRight: { justifyContent: "flex-start" } }}>
						<DefaultButton
							text="Return"
							onClick={() => {
								setQuoteWorkflowStep("ask-previous")
							}}
						/>
					</DialogFooter>
				</Dialog>
			)}
			{requoteGroup && (
				<RequoteDialog
					group={requoteGroup}
					onDismiss={() => {
						setQuoteWorkflowStep(undefined)
						setRequoteGroup(undefined)
					}}
					onSave={() => {
						setRefreshFlag(!refreshFlag)
						setRequoteGroup(undefined)
					}}
				/>
			)}
		</>
	)
}
